import { DownloadOutlined } from '@ant-design/icons';
import { keepPreviousData,useInfiniteQuery } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Sorter from '@/components/DataTable/Sorter';
import List from '@/components/List/List';
import queryKeys from '@/configs/react-query-keys';
import { FeedbackExport } from '@/features/Feedback/components/FeedbackExport/FeedbackExport';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { FEEDBACK_SORT_OPTIONS } from '@/features/Feedback/configs/feedback-sort-options';
import { useFeedbackSortState } from '@/features/Feedback/hooks/useFeedbackSortState';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import FeedbackCard from '@/features/Taxonomy/components/FeedbackCard/FeedbackCard';
import useAppInit from '@/hooks/domain/App/useAppInit';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { radius } from '@/styles/sizes';
import { GPT_DOMAIN } from '@/types/gptConst';

import SearchContentStyles from '../SearchContentStyles';

type Props = {
  alias: string;
  style?: React.CSSProperties;
  hideButton?: boolean;
  refetchKey?: string;
  useSkeleton?: boolean;
};

const PAGE_SIZE = 50;

const { ContentHeader } = SearchContentStyles;

const SearchFeedbackList = (props: Props) => {
  const { searchResult } = useSearchDetailContext();
  const appInit = useAppInit();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const [sortStatus, setSortStatus] = useFeedbackSortState();

  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedbackState>(null);

  const fetchSimpleFeedbacks = async (
    sortStatus: NotNullableSortStatusT,
    pageParam?: null | string,
  ) => {
    const res = await service.globalSearch.fetchSearchFeedbackList(
      props.alias,
      {
        query: {
          cursor: pageParam,
          sortby: sortStatus.sortBy,
          order: sortStatus.orderStatus,
        },
        payload: {},
      },
    );
    return {
      list: res.feedbacks,
      count: res.count,
      paginate_token: res.feedbacks[PAGE_SIZE - 1]?.paginate_token || null,
    };
  };

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        queryKeys.search.SEARCH_DETAIL,
        queryKeys.search.SEARCH_RESULT_FEEDBACK_LIST,
        props.alias,
        props.refetchKey,
        sortStatus.sortBy,
        sortStatus.orderStatus,
      ],
      queryFn: async ({ pageParam }) => {
        return await fetchSimpleFeedbacks(sortStatus, pageParam);
      },
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage?.paginate_token,
      enabled: appInit,
      placeholderData: keepPreviousData,
    });

  const feedbacks = useMemo(() => {
    return (
      data?.pages.flat().flatMap((page) => {
        return page.list;
      }) || []
    );
  }, [data]);

  const onFeedbackItemClick = (state: SelectedFeedbackState) => {
    if (!state) return;
    if (selectedFeedback?.feedbackAlias === state.feedbackAlias) {
      if (selectedFeedback?.blockAlias === state.blockAlias) {
        setSelectedFeedback(null);
      } else {
        setSelectedFeedback(state);
        setNewModal({
          alias: state.feedbackAlias,
          type: DETAIL_MODAL_TYPE.FEEDBACK,
          idx: 0,
          options: {
            focusedBlock: state.blockAlias,
          },
        });
      }
    } else {
      setSelectedFeedback(state);
      setNewModal({
        alias: state.feedbackAlias,
        type: DETAIL_MODAL_TYPE.FEEDBACK,
        idx: 0,
        options: {
          focusedBlock: state.blockAlias,
        },
      });
    }
  };

  const onExportFeedbackListClick = async (
    reason?: string,
    password?: string,
  ) => {
    await service.globalSearch.exportSearchFeedbackList({
      alias: props.alias,
      filterPayload: {},
      reason: reason,
      password: password,
    });
  };

  return (
    <Style g={24} style={props.style}>
      <Flex j="space-between" a="center" style={{ paddingRight: 32 }}>
        <div className="flex items-center gap-2">
          <ContentHeader>Feedback</ContentHeader>
        </div>

        <div className="flex items-center gap-2">
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
          {!props.hideButton && (
            <FeedbackExport onExportClick={onExportFeedbackListClick}>
              <Button variant="outlined" leftIcon={<DownloadOutlined />}>
                Export to CSV
              </Button>
            </FeedbackExport>
          )}
          <Button
            variant="outlined"
            sizeType="s"
            onClick={() => {
              setNewModal<FeedbackBlockListModalOptions>({
                alias: props.alias,
                type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
                idx: 0,
                options: {
                  queryKeys: [
                    ...queryKeys.search.SEARCH_DETAIL,
                    ...queryKeys.search.SEARCH_RESULT_FEEDBACK_LIST,
                    props.alias,
                  ],
                  title: `${searchResult.title} feedback`,
                  domain: 'global-search',
                  fetcher: fetchSimpleFeedbacks,
                  exportFetcher: async (reason?: string, password?: string) => {
                    await service.globalSearch.exportSearchFeedbackList({
                      alias: searchResult.alias,
                      filterPayload: {},
                      reason: reason,
                      password: password,
                    });
                  },
                  socketInfo: {
                    domain: GPT_DOMAIN.GLOBAL_SEARCH,
                    search: {
                      alias: searchResult.alias,
                      filterPayload: {},
                    },
                  },
                },
              });
            }}
          >
            View all
          </Button>
        </div>
      </Flex>
      {props.useSkeleton ? (
        <VFlex g={8} style={{ height: '100%', overflow: 'auto' }}>
          {feedbacks.map((feedback) => {
            return (
              <FeedbackCard
                key={feedback.alias}
                feedback={feedback}
                setSelectedFeedback={onFeedbackItemClick}
                selectedFeedback={selectedFeedback}
              />
            );
          })}
          <FeedbackSkeleton />
          <FeedbackSkeleton />
        </VFlex>
      ) : (
        <List
          data={feedbacks}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          placeholder={{
            message: 'No feedback',
          }}
          className="gap-2 pl-4 pb-6 h-full"
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          render={(feedback) => {
            return (
              <FeedbackCard
                key={feedback.alias}
                feedback={feedback}
                setSelectedFeedback={onFeedbackItemClick}
                selectedFeedback={selectedFeedback}
              />
            );
          }}
        />
      )}
    </Style>
  );
};

const Style = styled(VFlex)`
  width: 100%;
  height: 460px;
`;

const FeedbackSkeleton = () => {
  return (
    <SkeletonWrapper>
      <Flex j="space-between" padding="16px">
        <Flex g={8}>
          <Skeleton.Node active style={{ width: 45, height: 22 }} />
          <Skeleton.Node active style={{ width: 158, height: 16 }} />
        </Flex>
        <Flex g={8}>
          <Skeleton.Node active style={{ width: 52, height: 12 }} />
          <Skeleton.Node active style={{ width: 52, height: 12 }} />
          <Skeleton.Node active style={{ width: 52, height: 12 }} />
        </Flex>
      </Flex>
      <Divider />
      <VFlex g={22} padding="16px">
        <Skeleton.Node active style={{ width: 450, height: 22 }} />
        <Skeleton.Node active style={{ width: 320, height: 22 }} />
      </VFlex>
    </SkeletonWrapper>
  );
};

const SkeletonWrapper = styled(VFlex)`
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: ${radius.label}px;
`;

export default SearchFeedbackList;
